import React, { useEffect, useState } from "react";
import { ClipboardText, Trash, Plus, X, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";

const SPORTS = [
  "football", "basketball", "cricket", "tennis", "swimming",
  "athletics", "hockey", "rugby", "badminton", "volleyball",
  "boxing", "kabaddi", "general",
];

const emptyForm = {
  name: "", sport: "football", strengths: "", weaknesses: "",
  key_players: "", tactical_notes: "", next_match_date: "",
  location: "", last_meeting_result: "",
};

export default function SportsOpponents() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const load = () => {
    setLoading(true);
    api.get("/sports-coach/opponents")
      .then(({ data }) => setRows(data.items || []))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openNew = () => { setEditing(null); setForm(emptyForm); setShowForm(true); };
  const openEdit = (row) => {
    setEditing(row.opponent_id);
    setForm({
      name: row.name || "", sport: row.sport || "football",
      strengths: row.strengths || "", weaknesses: row.weaknesses || "",
      key_players: row.key_players || "", tactical_notes: row.tactical_notes || "",
      next_match_date: row.next_match_date || "", location: row.location || "",
      last_meeting_result: row.last_meeting_result || "",
    });
    setShowForm(true);
  };
  const cancel = () => { setShowForm(false); setEditing(null); setForm(emptyForm); };

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Opponent name is required"); return; }
    setSaving(true);
    try {
      const body = { ...form, name: form.name.trim(), next_match_date: form.next_match_date || null };
      if (editing) {
        await api.patch(`/sports-coach/opponents/${editing}`, body);
        toast.success("Updated");
      } else {
        await api.post("/sports-coach/opponents", body);
        toast.success("Opponent added");
      }
      cancel();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this opponent card?")) return;
    await api.delete(`/sports-coach/opponents/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div data-testid="sc-opponents">
      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
        <div>
          <div className="label-tiny text-slate-500">// OPPOSITION SCOUTING</div>
          <div className="text-sm text-slate-600">One card per opponent — strengths, weaknesses, key players, tactical notes.</div>
        </div>
        <button onClick={showForm ? cancel : openNew} className="flex items-center gap-2 bg-cyan-600 hover:bg-cyan-700 text-white text-xs label-tiny tracking-wider px-4 py-2" data-testid="sc-opponents-new-btn">
          {showForm ? <X size={14} /> : <Plus size={14} />} {showForm ? "CANCEL" : "NEW OPPONENT"}
        </button>
      </div>

      {showForm && (
        <div className="border border-slate-200 bg-white p-4 mb-6 space-y-3" data-testid="sc-opponents-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input className="border border-slate-300 px-3 py-2 text-sm bg-white" placeholder="Opponent name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="sc-opponent-name" />
            <select value={form.sport} onChange={(e) => setForm({ ...form, sport: e.target.value })} className="border border-slate-300 px-3 py-2 text-sm bg-white" data-testid="sc-opponent-sport">
              {SPORTS.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ").toUpperCase()}</option>)}
            </select>
            <input type="date" className="border border-slate-300 px-3 py-2 text-sm bg-white" value={form.next_match_date} onChange={(e) => setForm({ ...form, next_match_date: e.target.value })} data-testid="sc-opponent-date" />
            <input className="border border-slate-300 px-3 py-2 text-sm bg-white" placeholder="Location (home / away / venue)" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
          </div>
          <textarea className="w-full border border-slate-300 px-3 py-2 text-sm bg-white" rows={2} placeholder="Strengths — what do they do well?" value={form.strengths} onChange={(e) => setForm({ ...form, strengths: e.target.value })} data-testid="sc-opponent-strengths" />
          <textarea className="w-full border border-slate-300 px-3 py-2 text-sm bg-white" rows={2} placeholder="Weaknesses — how can we exploit them?" value={form.weaknesses} onChange={(e) => setForm({ ...form, weaknesses: e.target.value })} data-testid="sc-opponent-weaknesses" />
          <textarea className="w-full border border-slate-300 px-3 py-2 text-sm bg-white" rows={2} placeholder="Key players (name · role · threat)" value={form.key_players} onChange={(e) => setForm({ ...form, key_players: e.target.value })} />
          <textarea className="w-full border border-slate-300 px-3 py-2 text-sm bg-white" rows={3} placeholder="Tactical notes / game plan" value={form.tactical_notes} onChange={(e) => setForm({ ...form, tactical_notes: e.target.value })} data-testid="sc-opponent-tactical" />
          <input className="w-full border border-slate-300 px-3 py-2 text-sm bg-white" placeholder="Last meeting result (optional)" value={form.last_meeting_result} onChange={(e) => setForm({ ...form, last_meeting_result: e.target.value })} />
          <div className="flex justify-end">
            <button onClick={submit} disabled={saving} className="bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 text-white text-xs label-tiny tracking-wider px-4 py-2" data-testid="sc-opponent-submit">
              {saving ? "SAVING…" : editing ? "UPDATE" : "SAVE"}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="border border-dashed border-slate-300 p-8 text-center" data-testid="sc-opponents-empty">
          <ClipboardText size={28} weight="duotone" className="text-slate-400 mx-auto mb-2" />
          <div className="text-sm text-slate-500">No opponents scouted yet. Add your first opposition.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {rows.map((o) => (
            <div key={o.opponent_id} className="border border-slate-200 bg-white p-4" data-testid={`sc-opponent-card-${o.opponent_id}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div>
                  <div className="font-heading text-lg">{o.name}</div>
                  <div className="text-[10px] tracking-widest text-cyan-700">
                    {(o.sport || "").replace(/_/g, " ").toUpperCase()}
                    {o.next_match_date && <> · NEXT: {new Date(o.next_match_date).toDateString()}</>}
                    {o.location && <> · {o.location.toUpperCase()}</>}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => openEdit(o)} className="text-slate-500 hover:text-cyan-700" data-testid={`sc-opponent-edit-${o.opponent_id}`}><PencilSimple size={16} /></button>
                  <button onClick={() => remove(o.opponent_id)} className="text-slate-500 hover:text-red-600" data-testid={`sc-opponent-delete-${o.opponent_id}`}><Trash size={16} /></button>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs mt-2">
                {o.strengths && (<div><div className="label-tiny text-emerald-700 mb-1">STRENGTHS</div><div className="text-slate-700 whitespace-pre-wrap">{o.strengths}</div></div>)}
                {o.weaknesses && (<div><div className="label-tiny text-red-700 mb-1">WEAKNESSES</div><div className="text-slate-700 whitespace-pre-wrap">{o.weaknesses}</div></div>)}
                {o.key_players && (<div className="md:col-span-2"><div className="label-tiny text-amber-700 mb-1">KEY PLAYERS</div><div className="text-slate-700 whitespace-pre-wrap">{o.key_players}</div></div>)}
                {o.tactical_notes && (<div className="md:col-span-2"><div className="label-tiny text-cyan-700 mb-1">TACTICAL NOTES</div><div className="text-slate-700 whitespace-pre-wrap">{o.tactical_notes}</div></div>)}
                {o.last_meeting_result && (<div className="md:col-span-2 text-slate-500">Last meeting: {o.last_meeting_result}</div>)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
